import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ViewChild, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { MessageService } from 'primeng/api';
import { GlobalServicesService } from 'src/app/services/global-services.service';
import { OtpService } from 'src/app/services/otp.service';
import { SharedServicesService } from 'src/app/services/shared-services.service';
import { CrispService } from 'src/app/services/crisp.service';
import { safeGetItem, safeSetItem, safeRemoveItem } from 'src/app/utils/safe-storage';

@Component({
    selector: 'app-otp-verification',
    templateUrl: './otp-verification.component.html',
    styleUrls: ['./otp-verification.component.css'],
    standalone: false
})
export class OtpVerificationComponent implements OnInit, OnChanges, OnDestroy {

  @ViewChild('otp_value') otp_value: any;

  @Input() candidateId: string = '';
  @Input() eventId: string = '';
  @Input() emailToVerify: string = '';
  @Input() mobileToVerify: string = '';
  @Input() stageId: string = '';
  @Input() interviewId: string = '';
  @Input() formType: string = '';
  @Output() otpVerified = new EventEmitter<boolean>();
  @Output() emailVerified = new EventEmitter<boolean>();
  @Output() mailVerified = new EventEmitter<boolean>();
  @Output() mobileVerified = new EventEmitter<boolean>();
  @Output() previousFormDetails = new EventEmitter<{ [key: string]: any }>();
  @Output() candidateDetails = new EventEmitter<{ [key: string]: any }>();

  otpValue: any;
  userName: string = '';
  infoMessage: any = {};
  intervalId: any;

  btnLoading: boolean = false;
  pageLoading: boolean = false;
  isVerifying: boolean = false;
  sendOtpState: boolean = true;
  verifyOtpState: boolean = false;
  verifyMail: boolean = false;
  verifyMobile: boolean = false;
  waitToResend: boolean = false;
  loadResend: boolean = false;
  clicked: boolean = false;
  invalidOTP: boolean = false;
  expiredOTP: boolean = false;
  displayInfoMessage: boolean = false;
  verifyByEmail: boolean = false;
  verifyByMobile: boolean = false;
  enableMobile: boolean = false;
  enableVerify: boolean = false;
  sendOtpDisabled: boolean = false;
  testNotAvailable: boolean = false;
  testErrorMessage: string = '';

  otpTimer: number = 0;
  countdown: string = '';
  redirectUrl: string = '';
  cooldownTimer: number = 0;
  cooldownInterval: any;

  candidateName: string = '';
  testName: string = '';
  testDuration: string = '';
  testId: string = '';
  verifyCategory: string = '';

  // Walk-in assessment mode (verifyCategory === 'walkin')
  shortCode: string = '';
  companyLogo: string = '';
  otpDigits: string[] = ['', '', '', '', '', ''];
  otpInputs = Array(6).fill(0);

  _selectedCategory: string = 'email';
  CompanyName: string = '';
  readonly otpResendCooldownMs = 3 * 60 * 1000;
  readonly otpDeliveryExpectationMinutes = 5;

  constructor(private otpService: OtpService,
    private activatedRoute: ActivatedRoute,
    private globalService: GlobalServicesService,
    private messageServices: MessageService,
    private sharedService: SharedServicesService,
    private crispService: CrispService) { }

  ngOnInit(): void {
    this.checkOtpCooldown();
    if (window.location.pathname === '/verification') {
      this.pageLoading = true;
      this.sharedService.metadata$.subscribe((companyDetails) => {
        if (companyDetails) {
          const rawName = companyDetails.name || '';
          this.CompanyName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
          this.pageLoading = false;
          this.crispEnabled = !!companyDetails.company_config?.crisp_chat;
          if (this.crispEnabled && !this.crispInitialized) {
            this.initCrisp();
          }
        }
      });
      this.activatedRoute.queryParamMap.subscribe((param) => {
        this.candidateId = param.get('candidateId') || '';
        this.candidateName = param.get('candidateName') || '';
        this.testName = param.get('testName') || '';
        this.testDuration = param.get('testDuration') || '';
        this.testId = param.get('test_id') || '';
        this.stageId = param.get('stageId') || '';
        this.verifyCategory = param.get('verifyCategory') || '';
        if (this.eventId === '') {
          this.eventId = param.get('eventId') || '';
        }
        this.emailToVerify = param.get('candidateEmail') || '';
      });
      this.redirectUrl = window.location.href.split('&redirect=')[1];
    } else if (window.location.pathname.startsWith('/assessment')) {
      this.verifyCategory = 'walkin';
      this.pageLoading = true;
      this.sendOtpState = true;
      this.verifyOtpState = false;
      this.sendOtpDisabled = false;
      this.sharedService.metadata$.pipe(takeUntil(this.destroy$)).subscribe((companyDetails) => {
        if (companyDetails) {
          const rawName = companyDetails.name || '';
          this.CompanyName = rawName.charAt(0).toUpperCase() + rawName.slice(1);
          this.companyLogo = companyDetails.logo || '';
          this.crispEnabled = !!companyDetails.company_config?.crisp_chat;
          if (this.crispEnabled && !this.crispInitialized) {
            this.initCrisp();
          }
        }
      });
      this.activatedRoute.paramMap.pipe(takeUntil(this.destroy$)).subscribe((param) => {
        this.shortCode = param.get('shortCode') || '';
        this.resolveWalkIn();
      });
    } else if (window.location.pathname === '/candidatePanel') {
      this.verifyCategory = 'interview';
      this.sharedService.metadata$.subscribe((companyDetails) => {
        if (companyDetails) {
          this.crispEnabled = !!companyDetails.company_config?.crisp_chat;
          if (this.crispEnabled && !this.crispInitialized) {
            this.initCrisp();
          }
        }
      });
    } else {
      this.isEmbedded = true;
      this.sharedService.metadata$.subscribe((companyDetails) => {
        if (companyDetails) {
          this.crispEnabled = !!companyDetails.company_config?.crisp_chat;
          if (this.crispEnabled && this.emailToVerify && !this.crispInitialized) {
            this.initCrisp();
          }
        }
      });
    }
  }

  private crispInitialized = false;
  private crispEnabled = false;
  private isEmbedded = false;
  private destroy$ = new Subject<void>();
  showCrispBtn = false;

  private initCrisp(): void {
    this.crispInitialized = true;
    this.crispService.load();
    this.showCrispBtn = this.crispService.isEnabled;
    const pageType = this.verifyCategory === 'test' ? 'test-otp'
      : this.verifyCategory === 'interview' ? 'interview-otp'
      : 'registration';
    this.crispService.setSessionContext(pageType, this.CompanyName || undefined);
    this.setCrispIdentity();
    this.crispService.chatOpened$.pipe(takeUntil(this.destroy$)).subscribe(() => this.showCrispBtn = false);
    this.crispService.chatClosed$.pipe(takeUntil(this.destroy$)).subscribe(() => this.showCrispBtn = true);
  }

  private setCrispIdentity(): void {
    if (!this.emailToVerify) return;
    this.crispService.setUserIdentity(this.emailToVerify, this.candidateName || undefined);
  }

  openCrispChat(): void {
    this.crispService.openChat();
  }

  checkOtpCooldown() {
    const cooldownExpiry = safeGetItem('otpCooldownExpiry');
    const otpSent = safeGetItem('otpSent');
    
    if (otpSent === 'true') {
      this.sendOtpState = false;
      this.verifyOtpState = true;
    }
    
    if (cooldownExpiry) {
      const remainingTime = parseInt(cooldownExpiry) - Date.now();
      if (remainingTime > 0) {
        this.cooldownTimer = Math.ceil(remainingTime / 1000);
        this.sendOtpDisabled = true;
        this.startCooldownTimer();
      } else {
        safeRemoveItem('otpCooldownExpiry');
      }
    }
  }

  startCooldownTimer() {
    this.cooldownInterval = setInterval(() => {
      this.cooldownTimer--;
      if (this.cooldownTimer <= 0) {
        clearInterval(this.cooldownInterval);
        this.sendOtpDisabled = false;
        safeRemoveItem('otpCooldownExpiry');
      }
    }, 1000);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['candidateId'] && this.candidateId !== '') {
      // this.getUserData();
    }
    if (changes['emailToVerify'] && this.emailToVerify !== '') {
      this.verifyByEmail = true;
      this.userName = "Candidate";

      if (this.crispEnabled) {
        if (!this.crispInitialized) {
          this.initCrisp();
        } else {
          this.setCrispIdentity();
        }
      }
    }
  }

  sendTestOtp = () => {
    if (!this.stageId) {
      this.pageLoading = false;
      this.testNotAvailable = true;
      this.testErrorMessage = 'This assessment link is invalid or no longer active.';
      return;
    }
    this.btnLoading = true;
    this.sendOtpDisabled = true;
    safeSetItem('otpCooldownExpiry', (Date.now() + this.otpResendCooldownMs).toString());
    this.cooldownTimer = Math.floor(this.otpResendCooldownMs / 1000);
    this.startCooldownTimer();
    const payload = {"email": this.emailToVerify}
    this.otpService.sendCandidateTestOtpFastApi( payload, this.stageId).subscribe({
      next: (res: any) => {
        this.btnLoading = false;
        if (res.body.detail === 'OTP sent successfully.') {
          safeSetItem('otpSent', 'true');
          this.infoMessage = { 
            icon: 'pi pi-check', 
            message: 'OTP has been sent to ' + this.emailToVerify, 
            bgColor: '#55BA45' 
          };
          this.showInfoMessage();
          this.sendOtpState = false;
          this.verifyOtpState = true;
          this.verifyMail = false;
          this.waitToResend = true;
          this.startOtpTimer();
        } else {
          this.infoMessage = { 
            icon: 'pi pi-times', 
            message: 'Something went wrong!', 
            bgColor: '#E24A47' 
          };
          this.showInfoMessage();
        }
      },
      error: (error) => {
        this.btnLoading = false;
        this.sendOtpDisabled = false;
        safeRemoveItem('otpCooldownExpiry');
        clearInterval(this.cooldownInterval);
        const errorMessage = error.message || error.error?.detail || 'Failed to send OTP';
        if (errorMessage && errorMessage !== 'Failed to send OTP') {
          this.messageServices.clear();
          this.testNotAvailable = true;
          this.testErrorMessage = errorMessage;
          this.pageLoading = false;
        } else {
          this.infoMessage = { 
            icon: 'pi pi-times', 
            message: errorMessage, 
            bgColor: '#E24A47' 
          };
          this.showInfoMessage();
        }
      },
    });
  }
  resolveWalkIn = () => {
    if (!this.shortCode) {
      this.pageLoading = false;
      this.testNotAvailable = true;
      this.testErrorMessage = 'This assessment link is invalid or no longer active.';
      return;
    }
    this.otpService.resolveWalkInStageFastApi(this.shortCode).subscribe({
      next: (res: any) => {
        const data = res.body || {};
        this.stageId = data.stage_uuid || '';
        this.eventId = data.event_uuid || '';
        this.testName = data.test_name || '';
        this.testDuration = data.test_duration != null ? String(data.test_duration) : '';
        this.pageLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.pageLoading = false;
        this.testNotAvailable = true;
        this.testErrorMessage = error.error?.detail || 'This assessment link is invalid or no longer active.';
      }
    });
  }


  checkWalkInEligibility = () => {
    if (!this.emailToVerify) return;
    this.btnLoading = true;
    const payload = { email: this.emailToVerify };
    this.otpService.sendCandidateTestOtpFastApi(payload, this.stageId, true).subscribe({
      next: () => {
        this.btnLoading = false;
        this.sendOtpState = false;
        this.verifyOtpState = true;
        if (this.crispEnabled) this.setCrispIdentity();
      },
      error: (error: HttpErrorResponse) => {
        this.btnLoading = false;
        const message = error.status === 404
          ? 'This email is not registered for this assessment. Please contact the administrator.'
          : (error.error?.detail || 'Unable to verify your email. Please try again.');
        this.infoMessage = { icon: 'pi pi-times', message, bgColor: '#E24A47' };
        this.showInfoMessage();
      }
    });
  }

  verifyWalkInKey = () => {
    this.clicked = true;
    if (!this.otpValue || this.otpValue.toString().length != 6) {
      this.invalidOTP = true;
      return;
    }
    this.btnLoading = true;
    this.invalidOTP = false;
    const payload = { email: this.emailToVerify, key: this.otpValue };
    this.otpService.verifyWalkInKeyFastApi(payload, this.shortCode).subscribe({
      next: (res: any) => {
        if (res.body?.link) {
          try {
            window.open(decodeURIComponent(res.body.link), '_self');
          } catch {
            this.btnLoading = false;
            this.invalidOTP = true;
          }
        } else {
          this.btnLoading = false;
          this.invalidOTP = true;
        }
      },
      error: () => {
        this.btnLoading = false;
        this.invalidOTP = true;
      }
    });
  }

  sendOtpByEmail = () => {
    if (this._selectedCategory == 'email') {
      this.btnLoading = true;
      this.sendOtpDisabled = true;
      safeSetItem('otpCooldownExpiry', (Date.now() + this.otpResendCooldownMs).toString());
      this.cooldownTimer = Math.floor(this.otpResendCooldownMs / 1000);
      this.startCooldownTimer();
      const payload = {"email": this.emailToVerify}
      const origin = this.formType === 'offer' ? 'offer' : this.formType === 'documentation' ? 'documentation' : 'registration';
      const apiToCall = this.verifyCategory === 'interview' 
      ? this.otpService.sendCandidateInterviewOtpFastApi(payload, this.stageId, this.interviewId)
      : this.otpService.sendCandidateOtpFastApi(payload, this.eventId, origin);

      apiToCall.subscribe({
         next: (res: any) => {
          if (res.body.detail == 'OTP sent successfully.') {
            safeSetItem('otpSent', 'true');
            this.infoMessage = { icon: 'pi pi-check', message: 'OTP has been sent', bgColor: '#55BA45' }
            this.showInfoMessage();
            this.btnLoading = false;
            this.sendOtpState = false;
            this.verifyOtpState = true;
            this.verifyMail = false;
            this.verifyMobile = true;
            this.waitToResend = true;
            this.startOtpTimer();
          }
        },
        error: (error: HttpErrorResponse) => {
          this.btnLoading = false;
          this.sendOtpDisabled = false;
          safeRemoveItem('otpCooldownExpiry');
          clearInterval(this.cooldownInterval);
          this.infoMessage = { icon: 'pi pi-times', message: error.error?.detail || 'Failed to send OTP', bgColor: '#E24A47' }
          this.showInfoMessage();
        }
      })
    } else {
      this.btnLoading = true;
      let payload = {
        "mobileNumber": this.mobileToVerify,
      }
      // this.otpService.sendOtpByMobile(payload, 'interview').subscribe(res => {
      //   if (res.data) {
      //     this.infoMessage = { icon: 'pi pi-check', message: 'OTP has been sent to ' + this.mobileToVerify, bgColor: '#55BA45' }
      //     this.showInfoMessage();
      //     this.btnLoading = false;
      //     this.sendOtpState = false;
      //     this.verifyOtpState = true;
      //     this.verifyMail = true;
      //     this.verifyMobile = false;
      //     this.waitToResend = true;
      //     this.startOtpTimer();
      //   } else {
      //     this.infoMessage = { icon: 'pi pi-times', message: 'Something went wrong!', bgColor: '#E24A47' }
      //     this.showInfoMessage();
      //   }
      // })
    }
  }

  verifyOtp = () => {
    if (this._selectedCategory == 'email') {
      this.clicked = true;
      
      if (this.otpValue.toString().length == 6) {
        this.btnLoading = true;
        this.invalidOTP = false;
        this.expiredOTP = false;
        let payload = { email: this.emailToVerify, otp: this.otpValue };
        if (this.verifyCategory === 'test'){
          this.otpService.verifyCandidateTestOtpFastApi(payload, this.stageId).subscribe({
            next:(res) => {
              if (res.body?.link){
                safeRemoveItem('otpCooldownExpiry');
                safeRemoveItem('otpSent');
                window.open(decodeURIComponent(res.body?.link), '_self');
              }
            },
            error: (error: HttpErrorResponse) => {
              this.btnLoading = false;
              this.invalidOTP = true;
            }
          })
        } else if (this.verifyCategory === 'interview') {
          this.isVerifying = true; 
          this.otpService.verifyCandidateInterviewOtpFastApi(payload, this.stageId, this.interviewId).subscribe({
            next: (res) => {
              this.btnLoading = false;
              this.isVerifying = false;
              if (res.body.access_token) {
                safeRemoveItem('otpCooldownExpiry');
                safeRemoveItem('otpSent');
                let accessToken = {
                  access_token: res.body.access_token,
                  token_type: "Bearer"
                }
                safeSetItem('token', JSON.stringify(accessToken));
                this.mailVerified.emit(true);
                this.invalidOTP = false;
                const interviewDetails = res?.body?.interview_details ?? {};
                const jitsiToken = res?.body?.jitsi_jwt_token;

                  if (jitsiToken) {
                    interviewDetails.session_id = jitsiToken.session_id;
                    interviewDetails.provider = jitsiToken.provider;
                    interviewDetails.hms_auth_token = jitsiToken.auth_token;
                    interviewDetails.hms_room_id = jitsiToken.room_id;
                    interviewDetails.ws_auth_token = res.body.access_token;
                  }

                this.candidateDetails.emit(interviewDetails);
                this.emailVerified.emit(true);
                this.clicked = false;
              } else {
                this.invalidOTP = true;
                this.messageServices.add({ severity: 'error', summary: 'Expired', detail: res.body.detail });
              }
            },
            error: (error: HttpErrorResponse) => {
              this.isVerifying = false;
              this.invalidOTP = true;
              this.btnLoading = false;
            }
          });

        } else {
          this.otpService.verifyCandidateOtpFastApi(payload, this.eventId).subscribe({
            next:(res) => {
              if(res.status == 200){
                this.btnLoading = false;
                if (res.body.access_token) {
                  safeRemoveItem('otpCooldownExpiry');
                  safeRemoveItem('otpSent');
                  let accessToken = {
                    access_token: res.body.access_token,
                    token_type: res.body.token_type
                  }
                  safeSetItem('token', JSON.stringify(accessToken));
                  this.previousFormDetails.emit(res?.body?.previous_form_details ?? {})
                  this.mailVerified.emit(true);
                  this.invalidOTP = false;
                  this.otpVerified.emit(true);
                  if (this.verifyByEmail) {
                    this.emailVerified.emit(true);
                  }
                  if (this.isEmbedded) {
                    this.showCrispBtn = false;
                  }
                  this.clicked = false;
                } else {
                  this.invalidOTP = true;
                  this.messageServices.add({ severity: 'error', summary: 'Expired', detail: res.body.detail });
                }
              }
            },
            error: (error: HttpErrorResponse) => {
              this.mailVerified.emit(false);
              this.invalidOTP = true;
              this.btnLoading = false;
            }
          });
          }
        }
        else {
          this.mailVerified.emit(false);
          this.invalidOTP = true;
        }
      }
    else {
      this.clicked = true;
      if (this.otpValue.toString().length == 6) {
        this.btnLoading = true;
        this.invalidOTP = false;
        this.expiredOTP = false;
        let payload = { mobileNumber: this.mobileToVerify, otp: Number(this.otpValue) };
        // this.otpService.verifyOtpByMobile(payload, 'interview').subscribe(res => {

        //   switch (res.data) {
        //     case 'success': {
        //       this.btnLoading = false;
        //       if (window.location.pathname === '/verification') {
        //         this.infoMessage = { icon: 'pi pi-check', message: 'Verification Successfull!', bgColor: '#55BA45' }
        //         this.mobileVerified.emit(true);
        //         this.invalidOTP = false;
        //         this.clicked = false;
        //       } else {
        //         this.infoMessage = { icon: 'pi pi-check', message: 'Verification Successfull!', bgColor: '#55BA45' }
        //         this.mobileVerified.emit(true);
        //         this.invalidOTP = false;
        //         this.clicked = false;
        //       }
        //     } break;
        //     case 'failed': {
        //       this.invalidOTP = true;
        //     } break;
        //     case 'expired': {
        //       this.expiredOTP = true;
        //     }
        //   }
        //   this.btnLoading = false;
        // })

      } else {
        this.mobileVerified.emit(true);
        this.invalidOTP = true;
      }
    }
  }

  resendOtp = () => {
    if (this.loadResend || this.sendOtpDisabled) return;
    this.loadResend = true;
    safeSetItem('otpCooldownExpiry', (Date.now() + this.otpResendCooldownMs).toString());
    this.cooldownTimer = Math.floor(this.otpResendCooldownMs / 1000);
    this.sendOtpDisabled = true;
    this.startCooldownTimer();
    if (this._selectedCategory == 'mobile') {
      let payload = {
        "mobileNumber": this.mobileToVerify,
      }
      // this.otpService.sendOtpByMobile(payload, 'interview').subscribe(res => {
      //   this.infoMessage = { icon: 'pi pi-check', message: 'OTP has been sent to ' + this.emailToVerify, bgColor: '#55BA45' }
      //   this.showInfoMessage();
      //   this.loadResend = false;
      //   this.waitToResend = true;
      //   this.startOtpTimer();
      // })
    } else {
      const payload = {"email": this.emailToVerify}
      const origin = this.formType === 'offer' ? 'offer' : this.formType === 'documentation' ? 'documentation' : 'registration';
      const apiToCall = this.verifyCategory === 'interview' 
        ? this.otpService.sendCandidateInterviewOtpFastApi(payload, this.stageId, this.interviewId)
        : this.verifyCategory === 'test'
          ? this.otpService.sendCandidateTestOtpFastApi(payload, this.stageId)
          : this.otpService.sendCandidateOtpFastApi(payload, this.eventId, origin);
      apiToCall.subscribe(res => {
        this.loadResend = false;
        this.infoMessage = { icon: 'pi pi-check', message: 'OTP has been sent', bgColor: '#55BA45' }
        this.showInfoMessage();
        this.waitToResend = true;
        this.startOtpTimer();
      });
    }
  }

  startOtpTimer() {
    this.otpTimer = 60;
    this.countdown = this.formatTime(this.otpTimer);
    this.waitToResend = true;

    this.intervalId = setInterval(() => {
      this.otpTimer--;
      this.countdown = this.formatTime(this.otpTimer);

      if (this.otpTimer === 0) {
        clearInterval(this.intervalId);
        this.waitToResend = false;
      }
    }, 1000);
  }

  formatTime(seconds: number): string {
    return `${seconds.toString().padStart(2, '0')}`;
  }

  restrictNumber(event: any) {
    let e: any = event || window.event;
    let key = e.keyCode || e.which;

    if (key == 13) {
      this.verifyOtp();
    }
    if (key < 48 || key > 57 || event.target.value.toString().length >= 6) {
      if (e.preventDefault) e.preventDefault();
      e.returnValue = false;
    }
  }

  showInfoMessage = () => {
    this.displayInfoMessage = true;
    setInterval(() => {
      this.displayInfoMessage = false;
      this.infoMessage = {};
    }, 10000);
  }

  otpValueChange(event: any) {
    if (event.length == 0 || event.length == 6) {
      this.invalidOTP = false;
      this.expiredOTP = false;
    }
  }
  onOtpInput(event: any, index: number) {
    const input = event.target;
    let value = input.value;
    value = value.replace(/\D/g, '');
    if (value.length > 1) value = value.slice(-1);
    this.otpDigits[index] = value;
    input.value = value;
  
    if (value && index < 5) {
      const next = input.nextElementSibling;
      if (next) next.focus();
    }
  
    this.otpValue = this.otpDigits.join('');
    this.verifyCheck(this.otpValue);
  }
  
  onOtpKeyDown(event: any, index: number) {
    const input = event.target;
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prev = input.previousElementSibling;
      if (prev) prev.focus();
    }
  }
  
  onOtpPaste(event: ClipboardEvent) {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const numbersOnly = pastedData.replace(/\D/g, '');
    if (numbersOnly.length === 6) {
      for (let i = 0; i < 6; i++) {
        this.otpDigits[i] = numbersOnly[i];
      }
      this.otpValue = this.otpDigits.join('');
      this.verifyCheck(this.otpValue);
      setTimeout(() => {
        const otpInputs = document.querySelectorAll('.otp-input') as NodeListOf<HTMLInputElement>;
        if (otpInputs[5]) otpInputs[5].focus();
      }, 0);
    }
  }
  verifyCheck(event:any){
    if(this.otpValue.length == 6 && !isNaN(Number(event))){
      this.enableVerify = true;
    }else{
      this.enableVerify = false;
    }
  }

  ngOnDestroy(): void {
    if (this.intervalId) clearInterval(this.intervalId);
    if (this.cooldownInterval) clearInterval(this.cooldownInterval);
    this.destroy$.next();
    this.destroy$.complete();
    this.crispService.hideChat();
  }
}
